import { Button, ModalV2 } from '@root/components'
import { TagPill } from '@root/components/TagPill/TagPill'
import { useBusinessContext } from '@root/context'
import { deleteTag, ITag } from '@root/query/tags'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  tag?: ITag
  isVisible: boolean
  handleOnClose: () => void
}

const TagDeleteForm: React.FC<Props> = ({ tag, isVisible, handleOnClose }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()

  const deleteTagMutation = useMutation({
    mutationFn: (tag: ITag) => deleteTag({ slug, tagId: tag.id }),
    ...{
      onSuccess: () => {
        toast.success(t('settings.tags.delete.success'))
        // Invalidating the whole slug to
        // refresh the has_tags value
        queryClient.invalidateQueries({ queryKey: [slug] })
      },
      onError: () => {
        toast.error(t('general.error'))
      }
    }
  })

  const handleOnDelete = () => {
    try {
      deleteTagMutation.mutate(tag)
      handleOnClose()
    } catch (error) {
      console.error(error)
      return null
    }
  }

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={handleOnClose}
      header={t('settings.tags.delete.title')}
      width={500}
      height={600}
      footer={
        <>
          <Button onClick={() => handleOnClose()}>{t('general.cancel')}</Button>
          <Button intent="danger" onClick={() => handleOnDelete()}>
            {t('general.delete')}
          </Button>
        </>
      }
    >
      <StyledContainer>
        <StyledText>{t('settings.tags.delete.description')}</StyledText>
        <StyledTagContainer>
          <TagPill tag={tag}></TagPill>
        </StyledTagContainer>
      </StyledContainer>
    </ModalV2>
  )
}

export default TagDeleteForm

const StyledText = styled.p`
  margin: 0;
`
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const StyledTagContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl}rem;
  display: flex;
  align-items: center;
  justify-content: center;
`
